import React, { useCallback, useEffect, useState } from "react";
import Form from 'react-jsonschema-form';
import { Button } from 'reactstrap';
import ReactDOM from 'react-dom';
import axios from 'axios';
import moment from 'moment-timezone';

import MeterPointSelector from '../../../MeterPointSelector/MeterPointSelector';
import VendorSearch from '../VendorSearch/VendorSearch';
import { meteringServiceChangeSchema, meteringServiceChangeUISchema } from '../../schemas/meteringServiceChangeSchema';
import { useProcessForm } from '../../hooks/useProcessForm';
import ExpiringAlert from '../../../ExpiringAlert';

const MeteringServiceChangeForm = ({ onClose, initialData }) => {
  const { formContext, updateFormContext, isLoading, setIsLoading } = useProcessForm(initialData || {});
  const [serviceReference, setServiceReference] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  useEffect(() => {
    if (selectedVendor && selectedVendor.mDMVendorTypeList && selectedVendor.mDMVendorTypeList.length > 0) {
      const vendorType = selectedVendor.mDMVendorTypeList[0];
      const contractReference = vendorType && vendorType.contractReference ? vendorType.contractReference : '';

      updateFormContext({ contract_reference: contractReference });
    } else {
      updateFormContext({ contract_reference: '' });
    }
  }, [selectedVendor, updateFormContext]);

  const handleChange = useCallback(
    ({ formData }) => {
      updateFormContext(formData);
    },
    [updateFormContext]
  );

  const transformErrors = useCallback((errors) => {
    return errors.map((error) => {
      switch (error.property) {
        case '.mpan_core':
          return { ...error, message: 'MPAN Core is a required property' };
        case '.metering_service_mpid':
          return { ...error, message: 'Metering Service MPID is a required property' };
        case '.effective_from':
          return { ...error, message: 'Effective From is a required property' };
        case '.appointment_scenario':
          return { ...error, message: 'Appointment Scenario is a required property' };
        default:
          return error;
      }
    });
  }, []);

    const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return null;
  
    const hasTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(dateTimeString);
  
    const parsedDate = hasTimezone
      ? moment.parseZone(dateTimeString)
      : moment.tz(dateTimeString, 'Europe/London');
  
    if (!parsedDate.isValid()) {
      return null;
    }
  
    return parsedDate
      .tz('Europe/London')
      .startOf('day')
      .format('YYYY-MM-DDTHH:mm:ssZ');
  };

  const transformFormDataToPayload = (formData) => {
    // Get the actual mdmIdentificationNumber for the API payload
    const meteringServiceMpid = selectedVendor
      ? selectedVendor.mdmIdentificationNumber
      : formData.metering_service_mpid;

    const payload = {
      mpan_core: parseInt(formData.mpan_core, 10),
      metering_service_mpid: meteringServiceMpid,
      effective_from: formatDateTime(formData.effective_from),
      appointment_scenario: formData.appointment_scenario,
    };

    // Add contract_reference if provided, otherwise add service reference if available
    if (formData.contract_reference && formData.contract_reference.trim()) {
      payload.contract_reference = formData.contract_reference.trim();
    }

    return payload;
  };

  const uiSchemaWithWidgets = {
    ...meteringServiceChangeUISchema,
    mpan_core: {
      ...meteringServiceChangeUISchema.mpan_core,
      'ui:widget': (props) => <MeterPointSelector {...props} placeholder='Select MPAN' includeAllStatuses={true} />,
    },
    metering_service_mpid: {
      ...meteringServiceChangeUISchema.metering_service_mpid,
      'ui:widget': (props) => (
        <VendorSearch
          {...props}
          placeholder='Select Metering Service Provider'
          onServiceReferenceUpdate={setServiceReference}
          onVendorSelect={setSelectedVendor}
        />
      ),
    },
  };

  const loadingIcon = isLoading ? (
    <span>
      <i className='fa fa-circle-o-notch fa-spin'></i>{' '}
    </span>
  ) : null;

  return (
    <div className='metering-service-change-form'>
      <Form
        schema={meteringServiceChangeSchema}
        uiSchema={uiSchemaWithWidgets}
        formContext={formContext}
        formData={formContext}
        transformErrors={transformErrors}
        showErrorList={false}
        onSubmit={({ formData }) => {
          setIsLoading(true);
          const payload = transformFormDataToPayload(formData);

          // eslint-disable-next-line no-console
          console.log('Submitting payload to BOP.Dataflows.StartMeteringServicesChange:', payload);

          axios
            .post('/publishMessage', payload, {
              headers: {
                Authorization: `Bearer ${localStorage.token}`,
              },
              params: { queue: 'BOP.Dataflows.StartMeteringServicesChange' },
            })
            .then((response) => {
              setIsLoading(false);
              if (response.data === 'Message sent') {
                ReactDOM.render(
                  <ExpiringAlert color='success' message='Metering Service Change request submitted successfully!' />,
                  document.getElementById('alert').appendChild(document.createElement('div'))
                );
                onClose();
              } else {
                ReactDOM.render(
                  <ExpiringAlert color='danger' message={response.data.message || 'Error submitting request'} />,
                  document.getElementById('alert').appendChild(document.createElement('div'))
                );
              }
            })
            .catch((error) => {
              setIsLoading(false);
              // eslint-disable-next-line no-console
              console.error('Error submitting metering service change request:', error);
              // eslint-disable-next-line no-console
              console.log('Publishing to queue BOP.Dataflows.StartMeteringServicesChange:', payload);
              ReactDOM.render(
                <ExpiringAlert color='danger' message='Error submitting request. Request logged to console.' />,
                document.getElementById('alert').appendChild(document.createElement('div'))
              );
            });
        }}
        onChange={handleChange}
      >
        <div>
          <Button type='submit' disabled={isLoading} color='primary'>
            {isLoading && loadingIcon}Submit Request
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button type='button' onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default MeteringServiceChangeForm;
